import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { GeneralLedgerView } from '../models/GeneralLedgerView';
import { WorkflowForm } from '../models/WorkflowForm';
import { LookupServices } from './lookup.service';
import { map } from 'rxjs/operators';
import {
	AbstractControl,
	FormArray,
	FormGroup,
	UntypedFormArray,
	UntypedFormBuilder,
	UntypedFormGroup,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { ClientsService } from './clients.service';
import { GlSegmentation, glSegmentations } from 'app/models/segment-labeling-gl-strings/GlSegmentation';
import { ValueFormatterParams } from 'ag-grid-community';
import { DeviceDetectorService } from 'ngx-device-detector';
import Swal from 'sweetalert2';

declare const moment: any;

/**
 * Interface for components using date picker blur handlers.
 * Components must have a property that tracks date validation state.
 */
interface DatePickerContext {
	[key: string]: any;
	areDatesValid?: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class UtilityService {
	static readonly ALLOWED_FILE_EXTENSIONS: string[] = [
		'.pdf',
		'.doc',
		'.docx',
		'.xls',
		'.xlsx',
		'.png',
		'.jpg',
		'.jpeg',
		'.jng',
		'.gif',
		'.bmp',
		'.tif',
		'.tiff',
		'.csv',
		'.txt',
		'.zip',
	];

	static readonly CLIENT_IMPLEMENTATION_DOCUMENT_EXTENSIONS: string[] = [
		...UtilityService.ALLOWED_FILE_EXTENSIONS,
		'.mp4',
	];

	static readonly EXTENSION_MIME_MAP: Record<string, string[]> = {
		'.pdf': ['application/pdf'],
		'.doc': ['application/msword'],
		'.docx': ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
		'.xls': ['application/vnd.ms-excel'],
		'.xlsx': ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
		'.png': ['image/png'],
		'.jpg': ['image/jpeg'],
		'.jpeg': ['image/jpeg'],
		'.jng': ['image/x-jng', 'image/jng'],
		'.gif': ['image/gif'],
		'.bmp': ['image/bmp'],
		'.tif': ['image/tiff'],
		'.tiff': ['image/tiff'],
		'.csv': ['text/csv', 'application/csv', 'text/plain'],
		'.txt': ['text/plain'],
		'.mp4': ['video/mp4', 'video/x-m4v'],
		'.zip': ['application/zip', 'application/x-zip-compressed', 'application/octet-stream'],
	};

	private readonly MAX_TOTAL_SIZE_MB = 50;
	private readonly MAX_TOTAL_SIZE_BYTES = this.MAX_TOTAL_SIZE_MB * 1024 * 1024;
	requiredFieldMap: { [key: string]: boolean } = {};

	constructor(
		private readonly lookupService: LookupServices,
		private readonly clientService: ClientsService,
		private readonly deviceService: DeviceDetectorService
	) {}

	getGlSegmentation(clientId: any): GlSegmentation {
		const segmentation = glSegmentations.find((t) => t.clientId === clientId);
		if (segmentation) {
			return segmentation;
		}
		return glSegmentations.find((t) => t.clientId === 'default');
	}

	isNullOrEmpty(myString: string): boolean {
		return myString == null || myString.trim() === '';
	}

	formatCurrency(currency: number) {
		return this.formatCurrencyWithPrecision(currency, 2);
	}

	formatCurrencyWithoutDollarSign(currency: number) {
		try {
			const converted = Number(currency);
			if (converted || converted === 0) {
				let changedVal = converted;
				if (converted < 0) {
					changedVal *= -1;
				}
				let value = Number(changedVal.toFixed(2)).toLocaleString();
				const checkFinal = value.split('.');
				if (checkFinal.length > 1) {
					if (checkFinal[1].length === 1) {
						value += '0';
					}
				}
				if (converted >= 0) {
					return `${value}`;
				} else {
					return `(${value})`;
				}
			}
		} catch (e) {
			console.log(e);
		}
		return '--';
	}

	// PI-1542: moment formats an invalid moment as the literal string 'Invalid date'.
	// Returning that to a caller lets it reach a form control and then the request body,
	// where the server fails to bind it to a DateTime. Return null instead so callers
	// get an empty value. The parameter type is widened to match what callers actually
	// pass: API payload dates arrive as strings, and both null and undefined are common.
	formatDate(val: Date | string | null | undefined): string | null {
		if (!val) {
			return null;
		}
		const parsed = moment.utc(val);
		return parsed.isValid() ? parsed.format('MM/DD/YYYY') : null;
	}

	formatIsoDateToMMDDYYYY(value: string | null): string | null {
		if (!value) {
			return null;
		}
		const parsed = moment(value, moment.ISO_8601, true);
		return parsed.isValid() ? parsed.format('MM/DD/YYYY') : null;
	}

	removeNumericFormatting(amount: any) {
		if (amount) {
			if (amount !== '' || amount !== undefined || amount !== 0 || amount !== '0' || amount !== null) {
				amount = amount.toString();
				while (amount.indexOf(',') >= 0) {
					amount = amount.replace(',', '');
				}
				return amount;
			}
		} else {
			return amount;
		}
	}

	formatNumeric(amount: any) {
		const reg = /\B(?=(\d{3})+(?!\d))/g;
		if (amount || amount === 0) {
			// Group only the integer part. Applied to the whole string the pattern also
			// reaches into the decimals, turning a millage rate of '7.1234' into '7.1,234'.
			const [integerPart, ...fractionParts] = amount.toString().replace('$', '').split('.');
			return [integerPart.replace(reg, ','), ...fractionParts].join('.');
		} else {
			return amount;
		}
	}

	truncate(str, n) {
		if (str) {
			return str.length > n ? str.substr(0, n - 1) + '...' : str;
		}

		return str;
	}

	isApprover(workflow: any, userId: string): boolean {
		if (workflow && workflow?.currentApprovers?.length > 0 && workflow.workflowStep !== 'Completed') {
			const item = workflow.currentApprovers.find((t) => t.userId === userId && !t.hasApproved);
			return item != null || item != undefined;
		} else {
			return false;
		}
	}

	hasApproved(workflow: any, userId: string): boolean {
		if (workflow && workflow?.currentApprovers?.length > 0 && workflow.workflowStep !== 'Completed') {
			const item = workflow.currentApprovers.find((t) => t.userId === userId);
			return item?.hasApproved;
		} else {
			return false;
		}
	}

	getFiscalYearFromDate(date: Date): number {
		if (!date || isNaN(new Date(date).getTime())) {
			return new Date().getFullYear();
		}
		const beginMonth = this.getFiscalYearMonthBegin();
		const currentMonth = new Date(date).getMonth() + 1;
		if (currentMonth >= beginMonth && beginMonth !== 1) {
			return new Date(date).getFullYear() + 1;
		}
		return new Date(date).getFullYear();
	}

	getFiscalYear(month: number, year: number): number {
		if (month == null || year == null || isNaN(month) || isNaN(year)) {
			return new Date().getFullYear();
		}
		const beginMonth = this.getFiscalYearMonthBegin();
		if (month >= beginMonth && beginMonth !== 1) {
			return year + 1;
		}
		return year;
	}

	getEffectiveYear(month, year) {
		const beginMonth = this.getFiscalYearMonthBegin();
		if (beginMonth === 1) {
			return year;
		}

		if (beginMonth <= month) {
			return year - 1;
		}
		return year;
	}

	currentFiscalYear(startAt = 1, endAt = 1): number[] {
		return this.currentFiscalYearDdl(new Date().getFullYear() - startAt, new Date().getFullYear() + endAt);
	}

	currentFiscalYearDdl(startYear = 2020, endYearToHave = -1): number[] {
		let currentYear = new Date().getFullYear() + 1;
		const currentDate = new Date();
		const monthBegin = this.getFiscalYearMonthBegin();
		const currentMonth = currentDate.getMonth() + 1;
		if (currentMonth >= monthBegin && monthBegin !== 1) {
			currentYear++;
		}

		if (endYearToHave > 0) {
			currentYear = endYearToHave;
		}

		const years = [];

		while (startYear <= currentYear) {
			years.push(startYear);
			startYear++;
		}

		return years;
	}

	objectToQueryString(obj: Record<string, any>): string {
		const str: string[] = [];
		for (const p in obj) {
			if (Object.prototype.hasOwnProperty.call(obj, p) && obj[p] !== null && obj[p] !== undefined) {
				str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
			}
		}
		return str.join('&');
	}

	glDisplay(gl: GeneralLedgerView) {
		const glSettings = localStorage.getItem('GLDisplay');
		if (glSettings && glSettings === 'Name') {
			return gl.name;
		} else {
			return `${gl.id} - ${gl.name}`;
		}
	}

	hasSegmentation(segmentationId: string | null | undefined): segmentationId is string {
		if (segmentationId === null || segmentationId === undefined) {
			return false;
		}
		const trimmed = `${segmentationId}`.trim();
		return trimmed !== '' && trimmed.toLowerCase() !== 'null' && trimmed.toLowerCase() !== 'undefined';
	}

	getSegmentationParts(segmentationId: string | null | undefined): string[] {
		return this.hasSegmentation(segmentationId) ? segmentationId.split('-') : [];
	}

	buildGlAccountLabel(
		idOrCode: string | null | undefined,
		segmentationId: string | null | undefined,
		name: string | null | undefined
	): string {
		const prefix = this.hasSegmentation(segmentationId) ? `${idOrCode}-${segmentationId}` : `${idOrCode}`;
		return `${prefix} - ${name}`;
	}

	// PI-1519: pickers write "{id}[-{segmentationId}] - {name}" into the search field on selection,
	// but the per-form filters only match id/name separately, so a full label yields zero options.
	// Fallback used by every GL account picker when its primary filter finds nothing.
	filterGlAccountsByLabel<T extends { id?: string; code?: string; name?: string; segmentationId?: string | null }>(
		accounts: T[] | null | undefined,
		searchValue: string | null | undefined
	): T[] {
		const list = accounts ?? [];
		const term = `${searchValue ?? ''}`.trim().toLowerCase();
		if (!term) {
			return list;
		}
		// "{codePart} - {namePart}": clients with deep segmentation have suffixes users never type in
		// full (e.g. '000-000-000-000-000'), so the code+segment side matches as a prefix instead.
		const separatorIndex = term.indexOf(' - ');
		const codePart = separatorIndex > -1 ? term.slice(0, separatorIndex).trim() : null;
		const namePart = separatorIndex > -1 ? term.slice(separatorIndex + 3).trim() : null;
		return list.filter((account) => {
			// Some pickers key the display label on `code` while the row also carries a non-display `id`
			// (e.g. the Journal Entry lookup); match a label built from either.
			const codes = [account.id, account.code].filter((value) => value != null);
			return codes.some((code) => {
				if (
					this.buildGlAccountLabel(code, account.segmentationId, account.name ?? '')
						.toLowerCase()
						.includes(term) ||
					this.buildGlAccountLabel(code, null, account.name ?? '')
						.toLowerCase()
						.includes(term)
				) {
					return true;
				}
				if (codePart && namePart) {
					const codeWithSegments = this.hasSegmentation(account.segmentationId)
						? `${code}-${account.segmentationId}`.toLowerCase()
						: `${code}`.toLowerCase();
					return codeWithSegments.startsWith(codePart) && `${account.name ?? ''}`.toLowerCase().includes(namePart);
				}
				return false;
			});
		});
	}

	segmentationMatches(a: string | null | undefined, b: string | null | undefined): boolean {
		const normalize = (value: string) =>
			this.getSegmentationParts(value)
				.map((part) => part.trim())
				.filter((part) => part !== '000');
		const partsA = normalize(a);
		const partsB = normalize(b);
		return partsA.length === partsB.length && partsA.every((part, index) => part === partsB[index]);
	}

	canSendForReview(item: WorkflowForm, userId: string) {
		return !this.hasApproved(item, userId) && this.isApprover(item, userId);
	}

	getRandomText(length) {
		const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.match(/./g);
		let text = '';

		for (let i = 0; i < length; i++) {
			text += charset[Math.floor(this.cryptoRandomGenerator() * charset.length)];
		}
		return text;
	}

	cryptoRandomGenerator() {
		const typedArray = new Uint8Array(1);
		const randomValue = crypto.getRandomValues(typedArray)[0];
		return randomValue / Math.pow(2, 8);
	}

	scrollToTop() {
		const element = document.getElementById('navigation');
		if (element) {
			element.scrollIntoView();
		}
	}

	scrollToElement(id: string) {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView();
		}
	}

	/**
	 * Centers the topmost visible field flagged with a validation error after a failed save.
	 * Deferred a tick so error classes applied during the same save pass land first.
	 */
	scrollToFirstError() {
		setTimeout(() => {
			// Controls inside a [hidden] block stay invalid in the DOM but have no layout box.
			const flagged = Array.from(document.querySelectorAll<HTMLElement>('.has-error, .ng-invalid')).filter(
				(element) => !(element instanceof HTMLFormElement) && element.getClientRects().length > 0
			);

			// Angular flags the formGroupName/formArrayName wrappers too; keep the fields.
			const fields = flagged.filter(
				(element) => !flagged.some((other) => other !== element && element.contains(other))
			);

			const target = fields.reduce<HTMLElement | null>(
				(highest, element) =>
					highest && highest.getBoundingClientRect().top <= element.getBoundingClientRect().top ? highest : element,
				null
			);
			if (!target) {
				return;
			}

			// On desktop .main-panel scrolls rather than the window, and scrollIntoView barely
			// moves a perfect-scrollbar container, so set scrollTop as AdminLayoutComponent does.
			const targetRect = target.getBoundingClientRect();
			const scroller = this.getScrollParent(target);
			if (scroller) {
				const scrollerRect = scroller.getBoundingClientRect();
				scroller.scrollTop += targetRect.top - scrollerRect.top - (scroller.clientHeight - targetRect.height) / 2;
			} else {
				window.scrollTo(0, Math.max(0, window.scrollY + targetRect.top - (window.innerHeight - targetRect.height) / 2));
			}
		});
	}

	/** Nearest scrollable ancestor, or null when only the window scrolls. */
	private getScrollParent(element: HTMLElement): HTMLElement | null {
		for (let parent = element.parentElement; parent; parent = parent.parentElement) {
			if (parent.scrollHeight > parent.clientHeight + 1) {
				// 'hidden' is intentional: perfect-scrollbar containers use it and scroll via scrollTop.
				const overflowY = getComputedStyle(parent).overflowY;
				if (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'hidden' || overflowY === 'overlay') {
					return parent;
				}
			}
		}
		return null;
	}

	testId(id: string) {
		if (id === 'create' || id === 'view' || id === 'edit') {
			return null;
		}

		if (id && id.indexOf('?') >= 0) {
			id = id.split('?')[0];
		}

		return id;
	}

	getMonthNameFromNumber(monthNumber: any): string {
		const monthNames = [
			'January',
			'February',
			'March',
			'April',
			'May',
			'June',
			'July',
			'August',
			'September',
			'October',
			'November',
			'December',
			'Period 13',
		];
		return monthNames[monthNumber - 1];
	}

	getFinancialYears(clientId: string): Observable<number[]> {
		return this.lookupService.getYears(clientId).pipe(
			map((res) => {
				return res;
			})
		);
	}

	getMonthsForYear(clientId: string, year: any): Observable<any[]> {
		return this.lookupService.getMonthsForYear(clientId, year).pipe(
			map((res) => {
				const months = [];
				if (res) {
					res.forEach((element) => {
						months.push({ id: element, name: this.getMonthNameFromNumber(element) });
					});
				}
				return months;
			})
		);
	}

	getMonths() {
		return [
			{
				name: 'January',
				value: 1,
			},
			{
				name: 'February',
				value: 2,
			},
			{
				name: 'March',
				value: 3,
			},
			{
				name: 'April',
				value: 4,
			},
			{
				name: 'May',
				value: 5,
			},
			{
				name: 'June',
				value: 6,
			},
			{
				name: 'July',
				value: 7,
			},
			{
				name: 'August',
				value: 8,
			},
			{
				name: 'September',
				value: 9,
			},
			{
				name: 'October',
				value: 10,
			},
			{
				name: 'November',
				value: 11,
			},
			{
				name: 'December',
				value: 12,
			},
		];
	}

	getUsState() {
		return [
			{
				name: 'Alabama',
				abbreviation: 'AL',
			},
			{
				name: 'Alaska',
				abbreviation: 'AK',
			},
			{
				name: 'American Samoa',
				abbreviation: 'AS',
			},
			{
				name: 'Arizona',
				abbreviation: 'AZ',
			},
			{
				name: 'Arkansas',
				abbreviation: 'AR',
			},
			{
				name: 'California',
				abbreviation: 'CA',
			},
			{
				name: 'Colorado',
				abbreviation: 'CO',
			},
			{
				name: 'Connecticut',
				abbreviation: 'CT',
			},
			{
				name: 'Delaware',
				abbreviation: 'DE',
			},
			{
				name: 'District Of Columbia',
				abbreviation: 'DC',
			},
			{
				name: 'Federated States Of Micronesia',
				abbreviation: 'FM',
			},
			{
				name: 'Florida',
				abbreviation: 'FL',
			},
			{
				name: 'Georgia',
				abbreviation: 'GA',
			},
			{
				name: 'Guam',
				abbreviation: 'GU',
			},
			{
				name: 'Hawaii',
				abbreviation: 'HI',
			},
			{
				name: 'Idaho',
				abbreviation: 'ID',
			},
			{
				name: 'Illinois',
				abbreviation: 'IL',
			},
			{
				name: 'Indiana',
				abbreviation: 'IN',
			},
			{
				name: 'Iowa',
				abbreviation: 'IA',
			},
			{
				name: 'Kansas',
				abbreviation: 'KS',
			},
			{
				name: 'Kentucky',
				abbreviation: 'KY',
			},
			{
				name: 'Louisiana',
				abbreviation: 'LA',
			},
			{
				name: 'Maine',
				abbreviation: 'ME',
			},
			{
				name: 'Marshall Islands',
				abbreviation: 'MH',
			},
			{
				name: 'Maryland',
				abbreviation: 'MD',
			},
			{
				name: 'Massachusetts',
				abbreviation: 'MA',
			},
			{
				name: 'Michigan',
				abbreviation: 'MI',
			},
			{
				name: 'Minnesota',
				abbreviation: 'MN',
			},
			{
				name: 'Mississippi',
				abbreviation: 'MS',
			},
			{
				name: 'Missouri',
				abbreviation: 'MO',
			},
			{
				name: 'Montana',
				abbreviation: 'MT',
			},
			{
				name: 'Nebraska',
				abbreviation: 'NE',
			},
			{
				name: 'Nevada',
				abbreviation: 'NV',
			},
			{
				name: 'New Hampshire',
				abbreviation: 'NH',
			},
			{
				name: 'New Jersey',
				abbreviation: 'NJ',
			},
			{
				name: 'New Mexico',
				abbreviation: 'NM',
			},
			{
				name: 'New York',
				abbreviation: 'NY',
			},
			{
				name: 'North Carolina',
				abbreviation: 'NC',
			},
			{
				name: 'North Dakota',
				abbreviation: 'ND',
			},
			{
				name: 'Northern Mariana Islands',
				abbreviation: 'MP',
			},
			{
				name: 'Ohio',
				abbreviation: 'OH',
			},
			{
				name: 'Oklahoma',
				abbreviation: 'OK',
			},
			{
				name: 'Oregon',
				abbreviation: 'OR',
			},
			{
				name: 'Palau',
				abbreviation: 'PW',
			},
			{
				name: 'Pennsylvania',
				abbreviation: 'PA',
			},
			{
				name: 'Puerto Rico',
				abbreviation: 'PR',
			},
			{
				name: 'Rhode Island',
				abbreviation: 'RI',
			},
			{
				name: 'South Carolina',
				abbreviation: 'SC',
			},
			{
				name: 'South Dakota',
				abbreviation: 'SD',
			},
			{
				name: 'Tennessee',
				abbreviation: 'TN',
			},
			{
				name: 'Texas',
				abbreviation: 'TX',
			},
			{
				name: 'Utah',
				abbreviation: 'UT',
			},
			{
				name: 'Vermont',
				abbreviation: 'VT',
			},
			{
				name: 'Virgin Islands',
				abbreviation: 'VI',
			},
			{
				name: 'Virginia',
				abbreviation: 'VA',
			},
			{
				name: 'Washington',
				abbreviation: 'WA',
			},
			{
				name: 'West Virginia',
				abbreviation: 'WV',
			},
			{
				name: 'Wisconsin',
				abbreviation: 'WI',
			},
			{
				name: 'Wyoming',
				abbreviation: 'WY',
			},
		];
	}

	autoPayEnabled(clientId: string): Observable<{ displayAutoPay: boolean; autoPayDefaultValue: boolean }> {
		const purchasingPaymentsEnabled = localStorage.getItem('PurchasingPaymentsEnabled');
		if (purchasingPaymentsEnabled == null || (purchasingPaymentsEnabled === 'null' && clientId)) {
			return this.clientService.getClientFeatures(clientId).pipe(
				map((res) => {
					localStorage.setItem('PurchasingPaymentsEnabled', res.purchasingPaymentsEnabled);
					const displayAutoPay = res.purchasingPaymentsEnabled === 'true';
					const autoPayDefaultValue = displayAutoPay ? this.getAutoPayDefaultValue() : false;
					return { displayAutoPay, autoPayDefaultValue };
				})
			);
		} else {
			const displayAutoPay = purchasingPaymentsEnabled === 'true';
			const autoPayDefaultValue = displayAutoPay ? this.getAutoPayDefaultValue() : false;
			return of({ displayAutoPay, autoPayDefaultValue });
		}
	}

	private getAutoPayDefaultValue(): boolean {
		const purchasingPaymentsDefaultValue = localStorage.getItem('PurchasingPaymentsDefaultValue');
		return purchasingPaymentsDefaultValue ? purchasingPaymentsDefaultValue === 'true' : false;
	}

	round(num: number) {
		const m = Number((Math.abs(num) * 100).toPrecision(15));
		return (Math.round(m) / 100) * Math.sign(num);
	}

	decimalPlaces(num: number) {
		const match = ('' + String(num)).match(/(?:\.(\d+))?(?:[eE]([+-]?\d+))?$/);
		if (!match) {
			return 0;
		}
		return Math.max(
			0,
			// Number of digits right of decimal point.
			(match[1] ? match[1].length : 0) -
				// Adjust for scientific notation.
				(Number(match[2]) ? +Number(match[2]) : 0)
		);
	}

	getCurrentStep(item: WorkflowForm) {
		const currentStep = item?.workflowSteps?.find((t) => t.isCurrent);
		let stepName = '';
		if (currentStep != null) {
			stepName = currentStep.name;
		}

		return stepName;
	}

	getEmployeeForm(fb: UntypedFormBuilder): UntypedFormGroup {
		return fb.group({
			firstName: [null, [Validators.required]],
			lastName: [null],
			title: [null, [Validators.required]],
			departmentId: [null],
			isSalary: [true, [Validators.required]],
			allocationPercentage: [100, [Validators.required]],
			payRate: [null, [Validators.required]],
			otherRateChanges: [null],
			overTimeAllocated: [null],
			groupInsurance: [null],
			retirementType: [null],
			hourPerYearBase: [2080, [Validators.required]],
			specialPay: [null],
			healthType: [null],
			hasDental: [null],
			hasLife: [null],
			workersCompRateType: [null],
			id: [null],
			account: [null],
			pbaStep: [0],
			longevityPay: [null],
			meritPay: [null],
			hasCostOfLiving: [false],
			wages: [],
			name: [],
		});
	}

	convertToDate(dateStr: any) {
		if (!dateStr || dateStr == '0001-01-01T00:00:00Z') {
			return '';
		}
		if (dateStr instanceof Date) {
			return new Date(
				dateStr.getFullYear(),
				dateStr.getMonth(),
				dateStr.getDate(),
				dateStr.getHours(),
				dateStr.getMinutes(),
				dateStr.getSeconds(),
				dateStr.getMilliseconds()
			);
		}

		const date = new Date(dateStr);
		return moment(date).format('MM/DD/YYYY');
	}

	convertToDateTime(dateStr: any) {
		if (!dateStr || dateStr == '0001-01-01T00:00:00Z') {
			return '';
		}

		return moment(dateStr).format('MM/DD/YYYY HH:mm:ss');
	}

	convertToDateFromUTC(dateStr) {
		if (!dateStr) {
			return '';
		}
		if (dateStr instanceof Date) {
			return new Date(
				dateStr.getFullYear(),
				dateStr.getMonth(),
				dateStr.getDate(),
				dateStr.getHours(),
				dateStr.getMinutes(),
				dateStr.getSeconds(),
				dateStr.getMilliseconds()
			);
		}

		return moment(dateStr).utc().format('MM/DD/yyyy');
	}

	formatPhone(phone) {
		if (!phone) {
			return '';
		}
		return phone.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
	}

	getBenefitsDisplayValue(method: any, contribution: any) {
		if (contribution && method) {
			return method === 1 || method === 20 || method === 40
				? `${this.formatCurrency(contribution)}`
				: `${contribution}%`;
		} else {
			return '';
		}
	}

	private readonly monthNames = [
		'Jan',
		'Feb',
		'Mar',
		'Apr',
		'May',
		'Jun',
		'Jul',
		'Aug',
		'Sep',
		'Oct',
		'Nov',
		'Dec',
		'Period 13',
	];

	getMonthName(month: number) {
		if (month <= 0) {
			month = 1;
		} else if (month > 13) {
			month = 13;
		}
		return this.monthNames[month - 1];
	}

	retrieveConfiguration(clientId: string, callback: () => void, override = true) {
		if (!localStorage.getItem('FiscalYearMonthBegin')) {
			override = true;
		}
		if (override) {
			localStorage.removeItem('GLDisplay');
			localStorage.removeItem('PurchasingPaymentsEnabled');
			localStorage.removeItem('PurchasingPaymentsDefaultValue');
			localStorage.removeItem('FiscalYearMonthBegin');
			localStorage.removeItem('ReimbursementMileageRate');
			localStorage.removeItem('HasParentFund');
			localStorage.removeItem('HasParentDepartment');
			localStorage.removeItem('HasFSLineItem');
			const clientConfig = this.clientService.getClientFeatures(clientId).toPromise();
			const allConfigs = this.clientService.getClientConfigurations(clientId).toPromise();
			Promise.all([clientConfig, allConfigs]).then((data) => {
				const clientConfig = data[0];
				const configs = data[1];
				localStorage.setItem('PurchasingPaymentsEnabled', clientConfig.purchasingPaymentsEnabled ?? 'false');
				localStorage.setItem('PurchasingPaymentsDefaultValue', clientConfig.purchasingPaymentsDefaultValue ?? 'false');
				localStorage.setItem('FiscalYearMonthBegin', clientConfig.fiscalYearMonthBegin.toString());
				localStorage.setItem('BenefitsEnrollmentPeriod', clientConfig.benefitsEnrollmentPeriod);
				localStorage.setItem('ClientJobPortalUrl', clientConfig.clientJobPortalUrl);
				localStorage.setItem('ElaboratedJobApplicantDetails', clientConfig.elaboratedJobApplicantDetails);
				localStorage.setItem('IsTimesheetIPRestricted', clientConfig.isTimesheetIPRestricted ?? 'false');
				localStorage.setItem('BenefitsEnrollmentMessage', clientConfig.benefitsEnrollmentMessage);
				localStorage.setItem('ReimbursementMileageRate', clientConfig.reimbursementMileageRate);
				localStorage.setItem('HasParentFund', clientConfig.hasParentFund ?? 'false');
				localStorage.setItem('HasParentDepartment', clientConfig.hasParentDepartment ?? 'false');
				localStorage.setItem('HasFSLineItem', clientConfig.hasFSLineItem ?? 'false');
				localStorage.setItem(
					'AllowEntryPostIfPeriodIsClosedCheckboxAutoChecked',
					clientConfig.allowEntryPostIfPeriodIsClosedCheckboxAutoChecked ?? 'false'
				);
				localStorage.setItem(
					'AllowEntryPostIfPeriodIsClosedCheckboxEditable',
					clientConfig.allowEntryPostIfPeriodIsClosedCheckboxEditable ?? 'false'
				);
				if (configs && configs.length > 0) {
					const glConfig = configs.find((t) => t.configurationType === 'GLDisplay');
					if (glConfig?.value) {
						localStorage.setItem('GLDisplay', glConfig.value);
					}
				}

				if (callback) {
					callback();
				}
			});
		}
	}

	getFiscalYearMonthBegin(): number {
		const fiscalYearMonthBegin = localStorage.getItem('FiscalYearMonthBegin');
		if (fiscalYearMonthBegin) {
			const parsed = parseInt(fiscalYearMonthBegin, 10);
			if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) {
				return parsed;
			}
		}
		return 10; // defaulting to October
	}

	defaultExcelExportParams = {
		processCellCallback: (params) => {
			const colDef = params.column.getColDef();
			// try to reuse valueFormatter from the colDef
			if (colDef.valueFormatter) {
				const valueFormatterParams: ValueFormatterParams = {
					...params,
					data: params.node.data,
					node: params.node!,
					colDef: params.column.getColDef(),
				};
				return colDef.valueFormatter(valueFormatterParams);
			}
			return params.value;
		},
	};

	excelStyles = [
		{
			id: 'numberType',
			numberFormat: {
				format: '0',
			},
		},
		{
			id: 'currencyFormat',
			numberFormat: {
				format: '$#,##0.00;$[red](#,##0.00)',
			},
		},
		{
			id: 'negativeInBrackets',
			numberFormat: {
				format: '$[blue] #,##0;$ [red](#,##0)',
			},
		},
		{
			id: 'booleanType',
			dataType: 'Boolean',
		},
		{
			id: 'stringType',
			dataType: 'String',
		},
		{
			id: 'dateType',
			dataType: 'DateTime',
		},
	];

	setEffectiveCalendar(e: any, month: any, fiscalYear: any) {
		e.val('');
		if (Boolean(month)) {
			e.prop('disabled', false);
			const year = this.getEffectiveYear(month, fiscalYear);
			const effectiveStartDate = new Date(year, month - 1);
			const effectiveLastDate = new Date(effectiveStartDate.getFullYear(), effectiveStartDate.getMonth() + 1, 0);
			$(function () {
				if (e.hasClass('hasDatepicker')) {
					e.datepicker('option', {
						dateFormat: 'mm/dd/yy',
						minDate: effectiveStartDate,
						maxDate: effectiveLastDate,
						setDate: effectiveStartDate,
					});
				} else {
					e.datepicker({
						dateFormat: 'mm/dd/yy',
						minDate: effectiveStartDate,
						maxDate: effectiveLastDate,
						setDate: effectiveStartDate,
					});
				}
			});
		} else {
			e.prop('disabled', true);
			e.val('');
		}
	}

	verifyEffectiveDate(effectiveDate: any, fiscalmonth: any, fiscalYear: any) {
		const year = this.getEffectiveYear(fiscalmonth, fiscalYear);
		if (effectiveDate) {
			const date = new Date(effectiveDate);
			return date.getFullYear() === parseInt(year) && date.getMonth() + 1 === parseInt(fiscalmonth);
		}
	}

	getFormValidationErrors(form: UntypedFormGroup) {
		Object.keys(form.controls).forEach((key) => {
			const controlErrors: ValidationErrors = form.get(key).errors;
			if (controlErrors != null) {
				Object.keys(controlErrors).forEach((keyError) => {
					console.info('Key control: ' + key + ', keyError: ' + keyError + ', err value: ', controlErrors[keyError]);
				});
			}
		});
	}

	formateJson(obj: any[]) {
		const newList = [];
		obj.forEach((elsement) => {
			let key;
			const keys = Object.keys(elsement);
			let n = keys.length;
			const newobj = {};
			while (n--) {
				key = keys[n];
				newobj[key.toLowerCase()] = elsement[key];
			}
			newList.push(newobj);
		});
		return newList;
	}

	setReactiveForm(employeeForm: UntypedFormGroup, item: any) {
		Object.keys(item).forEach((key) => {
			if (employeeForm.controls[key]) {
				employeeForm.get(key).setValue(item[key]);
			}
		});
		return employeeForm;
	}

	getFormsAll() {
		return [
			{ id: 'gl-account', name: 'Account Management' },
			{ id: 'bank-reconciliation', name: 'Bank Reconciliation' },
			{ id: 'bid-form', name: 'Bid Form' },
			{ id: 'budget-transfer', name: 'Budget Transfer' },
			{ id: 'budget-amend', name: 'Budget Amendment' },
			{ id: 'business-license', name: 'Business License' },
			{ id: 'addition-form', name: 'Capital Assets Addition' },
			{ id: 'disposal-form', name: 'Capital Assets Disposal' },
			{ id: 'transfer-form', name: 'Capital Assets Transfer' },
			{ id: 'cashiering-report', name: 'Cashiering Form' },
			{ id: 'change-order', name: 'Change Order Form' },
			{ id: 'check-request', name: 'Check Request Form' },
			{ id: 'contract-approval', name: 'Contract Approval Form' },
			{ id: 'cc-approval', name: 'Credit Card Approval (AMSkills)' },
			{ id: 'customer-form', name: 'Customer Form' },
			{ id: 'depreciation-schedule-form', name: 'Depreciation Schedule Form' },
			{ id: 'department-addition-form', name: 'Department Addition Form' },
			{ id: 'fund-addition-form', name: 'Fund Addition Form' },
			{ id: 'customer-invoice-form', name: 'Invoice Form' },
			{ id: 'invoice-approval', name: 'Invoice Approval (AMSkills)' },
			{ id: 'ap-invoice', name: 'Invoice Approval Form w/PO' },
			{ id: 'journal-entry', name: 'Journal Entry Form' },
			{ id: 'lease-evaluation', name: 'Lease Evaluation Form' },
			{ id: 'monthly-timesheet', name: 'Monthly Time Sheet Form' },
			{ id: 'newproject', name: 'New Project Form' },
			{ id: 'capital-budget', name: 'Next Year Capital Budget' },
			{ id: 'operating-expense-budget', name: 'Next Year Operating Expense Budget' },
			{ id: 'dpt-exp-budget', name: 'Next Year Operating Budget' },
			{ id: 'fund-rev-budget', name: 'Next Year Revenue Budget' },
			{ id: 'open-close-period', name: 'Open Close Period Form' },
			{ id: 'p-card', name: 'P Card Form' },
			{ id: 'personnel', name: 'Personnel Form' },
			{ id: 'project-close-form', name: 'Project Close Form (project mgt)' },
			{ id: 'project-grant-form', name: 'Project & Grant Form (MB)' },
			{ id: 'project-grant-task', name: 'Project & Grant Task (MB)' },
			{ id: 'project-grant-close-form', name: 'Project & Grant Close Form (MB)' },
			{ id: 'project-grant-reopen-form', name: 'Project & Grant Reopen Form (MB)' },
			{ id: 'project-mgt', name: 'Project Request (project mgt)' },
			{ id: 'project-open-form', name: 'Project Open Form (project mgt)' },
			{ id: 'funding-increase-form', name: 'Project Funding Increase Form (project mgt)' },
			{ id: 'payment-approval-form', name: 'Project Payment Approval Form (project mgt)' },
			{ id: 'project-mgt', name: 'Project Request (project mgt)' },
			{ id: 'purchasing', name: 'Purchase Requisition' },
			{ id: 'po-rollover-form', name: 'PO Rollover Form' },
			{ id: 'po-rollback-form', name: 'PO Rollback Form' },
			{ id: 'quick-invoice-form', name: 'Quick Invoice Form' },
			{ id: 'quick-pay', name: 'Quick Pay Form' },
			{ id: 'receipt-verification', name: 'Receipt Verification Form' },
			{ id: 'timesheet', name: 'Time Sheet Form' },
			{ id: 'vendor-form', name: 'Vendor Form' },
			{ id: 'void-reissue-form', name: 'Void Reissue Form' },
			{ id: 'job-application', name: 'Job Application Form' },
			{ id: 'employee-action-form', name: 'Employee Action Form' },
			{ id: 'time-off-request-form', name: 'Time Off Request Form' },
			{ id: 'weekly-timesheet', name: 'Weekly Time Sheet Form' },
			{ id: 'performance-evaluation', name: 'Performance Evaluation Form' },
			{ id: 'benefit-enrolment-form', name: 'Benefit Enrolment Form' },
			{ id: 'cash-receipt-form', name: 'Cash Receipt Form' },
			{ id: 'individual-payee-form', name: 'Individual Payee Form' },
			{ id: 'individual-payment-form', name: 'Individual Payment Form' },
			{ id: 'new-capital-lease-form', name: 'New Capital Lease Form' },
			{ id: 'new-debt-issuance-form', name: 'New Debt Issuance Form' },
			{ id: 'debt-service-payment-form', name: 'Debt Service Payment Form' },
			{ id: 'debt-covenant-compliance-form', name: 'Debt Covenant Compliance Form' },
			{ id: 'debt-investment-je-form', name: 'Debt & Investment JE Form' },
			{ id: 'investment-security-purchase-form', name: 'Investment Security Purchase Form' },
			{ id: 'user-form', name: 'User Form' },
			{ id: 'lease-modification-form', name: 'Lease Modification Form' },
			{ id: 'implementation-billing-ar', name: 'Billing & AR Implementation' },
			{ id: 'new-hire-form', name: 'New Hire Form' },
			{ id: 'implementation-general-ledger', name: 'General Ledger Implementation' },
			{ id: 'implementation-purchasing', name: 'Purchasing Implementation' },
			{ id: 'leave-balance-adjustment', name: 'Leave Balance Adjustment Form' },
			{ id: 'implementation-payment', name: 'Payment Implementation' },
			{ id: 'customer-refund-form', name: 'Customer Refund Form' },
			{ id: 'workflow-group-form', name: 'Workflow Group Form' },
			{ id: 'customer-invoice-adjustment-form', name: 'Invoice Adjustment Form' },
			{ id: 'timesheet-approval-form', name: 'TimeSheet Approval Form' },
			{ id: 'implementation-hr', name: 'Implementation HR' },
			{ id: 'travel-pre-approval', name: 'Travel Pre Approval' },
			{ id: 'travel-reimbursement', name: 'Travel Reimbursement' },
			{ id: 'special-pay-form', name: 'Special Pay Form' },
			{ id: 'tuition-reimbursement', name: 'Tuition Reimbursement' },
			{ id: 'purchasing-employee-form', name: 'Employee Form' },
			{ id: 'taxing-jurisdiction-form', name: 'Taxing Jurisdiction Form' },
			{ id: 'new-customer-form', name: 'New Customer Form' },
			{ id: 'new-property-form', name: 'New Property Form' },
			{ id: 'new-customer-account-form', name: 'New Customer Account Form' },
			{ id: 'implementation-payroll', name: 'Implementation Payroll' },
			{ id: 'leave-balance-payout-form', name: 'Leave Balance Payout Form' },
			{ id: 'other-tuition-reimbursement', name: 'Other Tuition Reimbursement' },
			{ id: 'other-tuition-reimbursement-payment', name: 'Other Tuition Reimbursement Payment' },
			{ id: 'bulk-cash-receipt-form', name: 'Bulk Cash Receipt Form' },
			{ id: 'cashiering-adjustment-form', name: 'Cashiering Adjustment Form' },
			{ id: 'vendor-information-change-request', name: 'Vendor Information Change Request Form' },
			{ id: 'contract-amendment', name: 'Contract Amendment Form' },
			{ id: 'parent-vendor-form', name: 'Parent Vendor Form' },
			{ id: 'new-hire-request-form', name: 'New Hire Request Form' },
			{ id: 'policy-form', name: 'Policy Form' },
			{ id: 'procedure-form', name: 'Procedure Form' },
			{ id: 'travel-reimbursement-payment', name: 'Travel Reimbursement' },
			{ id: 'cost-allocation-form', name: 'Cost Allocation Form' },
			{ id: 'customize-task-form', name: 'Customized Task Form' },
			{ id: 'project-grant-compliance-form', name: 'Project & Grant Compliance Form' },
			{ id: 'mass-invoice-creation-form', name: 'Mass Invoice Creation Form' },
		];
	}

	formatNumber(number) {
		const reg = /(\d)(?=(\d{3})+(?!\d))/g;
		return Number(Math.abs(number)).toFixed(2).toString().replace(reg, '$1,');
	}

	formatNumberWithNegative(number) {
		const reg = /(\d)(?=(\d{3})+(?!\d))/g;
		return Number(number).toFixed(2).toString().replace(reg, '$1,');
	}

	getOrganizationId(glAccount: string): number {
		let organizationId = 0;
		if (glAccount === null || glAccount === undefined || glAccount === '') {
			return 0;
		}
		const glArray = glAccount.split('.');
		if (glArray.length > 0) {
			organizationId = Number(glArray[1]);
		}
		return organizationId;
	}

	getEntityId(glAccount: string): number {
		let entityId = 0;
		if (glAccount === null || glAccount === undefined || glAccount === '') {
			return 0;
		}
		const glArray = glAccount.split('.');
		if (glArray.length > 0) {
			entityId = Number(glArray[0]);
		}
		return entityId;
	}

	benefitsEnrollmentPeriod() {
		const benefitsEnrollmentPeriod = localStorage.getItem('BenefitsEnrollmentPeriod');
		return benefitsEnrollmentPeriod === 'null' ? 'February 25, 2022' : benefitsEnrollmentPeriod;
	}

	clientJobPortalUrl() {
		return localStorage.getItem('ClientJobPortalUrl');
	}

	elaboratedJobApplicantDetails() {
		return localStorage.getItem('ElaboratedJobApplicantDetails');
	}

	getAccountTypeString(accountType): string {
		const val = accountType?.toString();
		switch (val) {
			case '1':
			case 'A':
				return 'Asset';
			case '2':
			case 'L':
				return 'Liability';
			case '3':
			case 'X':
			case 'U':
				return 'Unearned Revenue';
			case '4':
			case 'R':
				return 'Revenue';
			case '5':
			case 'E':
				return 'Expense';
			default:
				return val;
		}
	}

	getAccountTypeById(accountTypeId) {
		if (accountTypeId === 1) {
			return 'A';
		} else if (accountTypeId === 2) {
			return 'L';
		} else if (accountTypeId === 3) {
			return 'A';
		} else if (accountTypeId === 4) {
			return 'R';
		} else if (accountTypeId === 5) {
			return 'E';
		}
		return '';
	}

	approvalConfigNames = [
		'First',
		'Second',
		'Third',
		'Fourth',
		'Fifth',
		'Sixth',
		'Seventh',
		'Eighth',
		'Ninth',
		'Tenth',
		'Eleventh',
		'Twelfth',
		'Thirteenth',
		'Fourteenth',
		'Fifteenth',
		'Sixteenth',
		'Seventeenth',
		'Eighteenth',
		'Nineteenth',
		'Twentieth',
		'TwentyOne',
		'TwentyTwo',
		'TwentyThree',
		'TwentyFour',
		'TwentyFive',
	];

	downloaddocument(content, contentType, documentName) {
		const blob = this.base64toBlob(content, contentType);
		const link = document.createElement('a');
		link.href = window.URL.createObjectURL(blob);
		link.download = documentName;
		link.click();
	}

	base64toBlob(base64Data, contentType) {
		contentType = contentType || '';
		const sliceSize = 1024;
		const byteCharacters = atob(base64Data);
		const bytesLength = byteCharacters.length;
		const slicesCount = Math.ceil(bytesLength / sliceSize);
		const byteArrays = new Array(slicesCount);

		for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
			const begin = sliceIndex * sliceSize;
			const end = Math.min(begin + sliceSize, bytesLength);

			const bytes = new Array(end - begin);
			for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
				bytes[i] = byteCharacters[offset].charCodeAt(0);
			}
			byteArrays[sliceIndex] = new Uint8Array(bytes);
		}
		return new Blob(byteArrays, { type: contentType });
	}

	getDateOfAMonthInInt(month, year) {
		const days = [];
		const daysInMonth = moment(`${year}-${month}`, 'YYYY-MM').daysInMonth();
		for (let i = 1; i <= daysInMonth; i++) {
			days.push(i);
		}
		return days;
	}

	filterParams: {} = {
		comparator: function (filterLocalDateAtMidnight, cellValue) {
			const dateAsString = cellValue;
			let cellDate = null;
			if (dateAsString == null) {
				return -1;
			}
			const dateParts = dateAsString.split('/');
			if (dateAsString.includes('-')) {
				cellDate = moment(dateAsString.split('T')[0]).toDate();
			} else {
				cellDate = new Date(Number(dateParts[2]), Number(dateParts[0]) - 1, Number(dateParts[1]));
			}
			if (filterLocalDateAtMidnight.getTime() === cellDate.getTime()) {
				return 0;
			}
			if (cellDate < filterLocalDateAtMidnight) {
				return -1;
			}
			if (cellDate > filterLocalDateAtMidnight) {
				return 1;
			}
			return 0;
		},
		browserDatePicker: true,
		minValidYear: 2000,
	};

	getMonthOrder() {
		const fiscalMonthStart = this.getFiscalYearMonthBegin();
		const monthOrder = [];

		let current = 1;
		let currentMonth = fiscalMonthStart;
		while (current <= 12) {
			monthOrder.push({ month: currentMonth, index: current });

			if (currentMonth === 12) {
				currentMonth = 1;
			} else {
				currentMonth++;
			}

			current++;
		}

		return monthOrder;
	}

	// Compares at cent scale (round + strict equality) rather than a tolerance band: a genuine
	// one-cent difference must never match, and the server compares these amounts as exact decimals.
	checkMatchWithOneCentVariance(bankAmount: number, glAmount: number, isCheckWithNegative = false): boolean {
		const bank = Number(bankAmount);
		const gl = Number(glAmount);
		if (bankAmount == null || glAmount == null || Number.isNaN(bank) || Number.isNaN(gl)) {
			return false;
		}
		const left = isCheckWithNegative ? bank : Math.abs(bank);
		const right = isCheckWithNegative ? gl : Math.abs(gl);
		return this.round(left) === this.round(right);
	}

	/**
	 * Tolerance for a fixed-count allocation (e.g. 12 monthly amounts) that must sum to a single
	 * target total. Each item is independently rounded to the nearest cent, so the cumulative
	 * rounding error can be up to half a cent per item. checkMatchWithOneCentVariance is tuned
	 * for two-way comparisons and is used across 40+ unrelated forms (PO, AP, payments) — it must
	 * not be loosened for this case. This is a separate, scoped helper for many-way allocation
	 * sums (PS-158).
	 */
	checkAllocationSumWithinRoundingTolerance(sum: number, target: number, itemCount: number): boolean {
		if (!Number.isFinite(sum) || !Number.isFinite(target) || !Number.isInteger(itemCount) || itemCount <= 0) {
			return sum === target;
		}
		const diff = Math.abs(sum - target);
		const tolerance = itemCount * 0.005 + 0.0001;
		return diff <= tolerance;
	}

	isWithinPoTolerance(
		originalAmount: number,
		openAmount: number,
		transactionAmount: number,
		toleranceConfig: any
	): boolean {
		if (transactionAmount < 0) {
			return false;
		}
		if (!toleranceConfig?.isApplicable) {
			return transactionAmount <= openAmount;
		}
		let tolerance = 0;
		if (toleranceConfig.applicableFlat) {
			tolerance = toleranceConfig.flatValue;
		}
		if (toleranceConfig.applicablePercentage) {
			tolerance = (originalAmount * toleranceConfig.percentageValue) / 100;
		}
		if (toleranceConfig.applicableFlat && toleranceConfig.applicablePercentage) {
			const percentTolerance = (originalAmount * toleranceConfig.percentageValue) / 100;
			tolerance =
				toleranceConfig.applicableValue === 'High'
					? Math.max(percentTolerance, toleranceConfig.flatValue)
					: Math.min(percentTolerance, toleranceConfig.flatValue);
		}
		return transactionAmount <= openAmount + tolerance;
	}

	listErrors(form: UntypedFormGroup) {
		if (form.invalid) {
			Object.keys(form.controls).forEach((key) => {
				const errors = form.get(key).errors;
				if (errors) {
					Object.keys(errors).map((error) => {
						console.info(`${key} has ${error} error`);
					});
				}
			});
		}
	}

	numericCompareForSorting(valueA, valueB) {
		// Convert the string values to numbers for comparison
		const numA = parseFloat(valueA ?? 0);
		const numB = parseFloat(valueB ?? 0);

		if (numA < numB) {
			return -1;
		}
		if (numA > numB) {
			return 1;
		}
		return 0;
	}

	alphaNumericCompareForSorting(a, b) {
		// check Null
		a = a ?? '';
		b = b ?? '';

		// Check if both invoice numbers have letters
		const aHasLetters = /[a-zA-Z]/.test(a);
		const bHasLetters = /[a-zA-Z]/.test(b);

		if (aHasLetters && bHasLetters) {
			// If both have letters, sort numerically first
			const aNumeric = parseFloat(a);
			const bNumeric = parseFloat(b);
			if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
				if (aNumeric !== bNumeric) {
					return aNumeric - bNumeric;
				}
			}

			// If numerically equal, sort alphabetically
			return a.localeCompare(b, undefined, { sensitivity: 'case' });
		} else if (aHasLetters) {
			// If only 'a' has letters, 'b' comes first
			return 1;
		} else if (bHasLetters) {
			// If only 'b' has letters, 'a' comes first
			return -1;
		} else {
			// If neither has letters, sort numerically
			const aNumeric = parseFloat(a);
			const bNumeric = parseFloat(b);
			if (!isNaN(aNumeric) && !isNaN(bNumeric)) {
				return aNumeric - bNumeric;
			}

			// If neither is numeric, sort alphabetically
			return a.localeCompare(b, undefined, { sensitivity: 'case' });
		}
	}

	isMobileOrTablet(): boolean {
		return this.deviceService.isMobile() || this.deviceService.isTablet();
	}

	isTimeSheetIPRestricted(): boolean {
		const isTimesheetIPRestrictedValue = localStorage.getItem('IsTimesheetIPRestricted');
		return isTimesheetIPRestrictedValue ? isTimesheetIPRestrictedValue === 'true' : false;
	}

	allowEntryPostIfPeriodIsClosedCheckboxAutoChecked(): boolean {
		const allowEntryPostIfPeriodIsClosedCheckboxAutoCheckedValue = localStorage.getItem(
			'AllowEntryPostIfPeriodIsClosedCheckboxAutoChecked'
		);
		return allowEntryPostIfPeriodIsClosedCheckboxAutoCheckedValue
			? allowEntryPostIfPeriodIsClosedCheckboxAutoCheckedValue === 'true'
			: false;
	}

	allowEntryPostIfPeriodIsClosedCheckboxEditable(): boolean {
		const allowEntryPostIfPeriodIsClosedCheckboxEditableValue = localStorage.getItem(
			'AllowEntryPostIfPeriodIsClosedCheckboxEditable'
		);
		return allowEntryPostIfPeriodIsClosedCheckboxEditableValue
			? allowEntryPostIfPeriodIsClosedCheckboxEditableValue === 'true'
			: false;
	}

	getErrorMessage(err: any): string {
		if (typeof err === 'string') {
			return err;
		}
		if (typeof ProgressEvent !== 'undefined' && (err instanceof ProgressEvent || err?.error instanceof ProgressEvent)) {
			const event: ProgressEvent = err instanceof ProgressEvent ? err : (err.error as ProgressEvent);
			if (event.type === 'timeout') {
				return 'The request timed out. Please try again.';
			}
			if (event.type === 'abort') {
				return 'The request was cancelled. Please try again.';
			}
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				return 'You appear to be offline. Please check your internet connection and try again.';
			}
			return 'A connection error occurred. Please try again.';
		}
		let errorMessage = '';
		try {
			const error = JSON.parse(err);
			if (typeof error === 'object') {
				errorMessage = '<ul>';
				for (const property in error) {
					if (Object.prototype.hasOwnProperty.call(error, 'message')) {
						errorMessage = error['message'];
						const errorArray = errorMessage.split('-> ');
						if (Array.isArray(errorArray)) {
							errorMessage = '<ul>';
							errorArray.forEach((errorItem) => {
								errorMessage += '<li>' + errorItem + '</li>';
							});
						}
						break;
					} else {
						const errorArray = error[property];
						if (Array.isArray(errorArray)) {
							errorArray.forEach((errorItem) => {
								errorMessage += '<li>' + errorItem + '</li>';
							});
						}
					}
				}
				if (errorMessage.indexOf('<ul>') >= 0) {
					errorMessage += '</ul>';
				}
			}
		} catch {
			if (err?.status === 413) {
				return (
					err?.error?.message ||
					'The request size exceeds the maximum allowed limit. Please reduce the number or size of attachments and try again.'
				);
			}
			errorMessage =
				err.error?.message ||
				(typeof err.error === 'string' ? err.error : undefined) ||
				(typeof err.error?.text === 'string' ? err.error.text : undefined) ||
				err.message ||
				err.Message ||
				err.error ||
				(err.status != null ? String(err.status) : undefined) ||
				err;
		}
		return errorMessage;
	}

	benefitsEnrollmentMessage() {
		const benefitsEnrollmentMessage = localStorage.getItem('BenefitsEnrollmentMessage');
		return benefitsEnrollmentMessage === null || benefitsEnrollmentMessage === 'null'
			? 'Waive or enroll in benefits by'
			: benefitsEnrollmentMessage;
	}
	getLevelIdentifiers(
		fund: any,
		department: any,
		generalLedgerId: any,
		restrictionLevels: { E: string; O: string; A: string },
		accountDetail: any
	) {
		const levelIdentifiers = {
			E: null,
			O: null,
			A: null,

			fundId: null,
			fundTypeId: null,
			fundGroup: null,
			e4: null,
			e5: null,
			e6: null,

			departmentId: null,
			subFunctionId: null,
			functionId: null,
			o4: null,
			o5: null,
			o6: null,

			glId: null,
			subObjectCodeId: null,
			objectCodeId: null,
			character: null,
			accountType: null,
			a6: null,
		};

		if (restrictionLevels.E) {
			switch (restrictionLevels.E) {
				case 'E1':
					levelIdentifiers.E = fund || null;
					levelIdentifiers.fundId = fund || null;
					break;
				case 'E2':
					levelIdentifiers.E = accountDetail.fundType;
					levelIdentifiers.fundTypeId = accountDetail.fundType;
					break;
				case 'E3':
					levelIdentifiers.E = accountDetail.fundGroup;
					levelIdentifiers.fundGroup = accountDetail.fundGroup;
					break;
				case 'E4':
					levelIdentifiers.E = accountDetail.e4;
					levelIdentifiers.e4 = accountDetail.e4;
					break;
				case 'E5':
					levelIdentifiers.E = accountDetail.e5;
					levelIdentifiers.e5 = accountDetail.e5;
					break;
				case 'E6':
					levelIdentifiers.E = accountDetail.e6;
					levelIdentifiers.e6 = accountDetail.e6;
					break;
				default:
					levelIdentifiers.E = null;
					break;
			}
		}

		if (restrictionLevels.O) {
			switch (restrictionLevels.O) {
				case 'O1':
					levelIdentifiers.O = department || null;
					levelIdentifiers.departmentId = department || null;
					break;
				case 'O2':
					levelIdentifiers.O = accountDetail.subFunction;
					levelIdentifiers.subFunctionId = accountDetail.subFunction;
					break;
				case 'O3':
					levelIdentifiers.O = accountDetail.function;
					levelIdentifiers.functionId = accountDetail.function;
					break;
				case 'O4':
					levelIdentifiers.O = accountDetail.o4;
					levelIdentifiers.o4 = accountDetail.o4;
					break;
				case 'O5':
					levelIdentifiers.O = accountDetail.o5;
					levelIdentifiers.o5 = accountDetail.o5;
					break;
				case 'O6':
					levelIdentifiers.O = accountDetail.o6;
					levelIdentifiers.o6 = accountDetail.o6;
					break;
				default:
					levelIdentifiers.O = null;
					break;
			}
		}

		if (restrictionLevels.A) {
			switch (restrictionLevels.A) {
				case 'A1':
					levelIdentifiers.A = generalLedgerId || null;
					levelIdentifiers.glId = generalLedgerId || null;
					break;
				case 'A2':
					levelIdentifiers.A = accountDetail.subObjectCode;
					levelIdentifiers.subObjectCodeId = accountDetail.subObjectCode;
					break;
				case 'A3':
					levelIdentifiers.A = accountDetail.objectCode;
					levelIdentifiers.objectCodeId = accountDetail.objectCode;
					break;
				case 'A4':
					levelIdentifiers.A = accountDetail.accountCharacter;
					levelIdentifiers.character = accountDetail.accountCharacter;
					break;
				case 'A5':
					levelIdentifiers.A = accountDetail.accountType;
					levelIdentifiers.accountType = accountDetail.accountType;
					break;
				case 'A6':
					levelIdentifiers.A = accountDetail.a6;
					levelIdentifiers.a6 = accountDetail.a6;
					break;
				default:
					levelIdentifiers.A = null;
					break;
			}
		}
		return levelIdentifiers;
	}

	isControlRequiredField(form_field: any) {
		if (!form_field.validator) {
			return false;
		}

		const validator = form_field.validator({} as AbstractControl);
		return validator?.required;
	}

	getSegmentDetails(account) {
		const buildSegmentDetail = (segmentId, segmentsData) => {
			const segment = segmentsData?.find((seg) => seg.subSegments?.some((subSegment) => subSegment.id === segmentId));
			if (!segment) return '';

			const subSegment = segment.subSegments?.find((subSegment) => subSegment.id === segmentId);
			if (!subSegment) return '';

			let detail = `${segment.name}: `;
			if (subSegment.idNumber) {
				detail += `${subSegment.idNumber} - `;
			}
			detail += subSegment.name;

			return detail;
		};

		const segmentDetails = [];
		if (account.segment1Id) {
			const detail = buildSegmentDetail(account.segment1Id, account.segmentsdata);
			if (detail) segmentDetails.push(detail);
		}
		if (account.segment2Id) {
			const detail = buildSegmentDetail(account.segment2Id, account.segmentsdata);
			if (detail) segmentDetails.push(detail);
		}
		if (account.segment3Id) {
			const detail = buildSegmentDetail(account.segment3Id, account.segmentsdata);
			if (detail) segmentDetails.push(detail);
		}

		return segmentDetails.join(', ');
	}

	getProjectDetails(account, projectId) {
		if (account?.projectGrantList) {
			const project = account.projectGrantList?.find((x) => x.projectGrantForm?.id === projectId)?.projectGrantForm;
			if (project) {
				return project.type + ' - ' + project.number + ' - ' + project.title;
			}
		}
		return '';
	}

	getProjectTaskDetails(account, projecttaskId) {
		if (account?.projectsGrantsTasks) {
			const project = account.projectsGrantsTasks?.find((x) => x.id === projecttaskId);
			if (project) {
				return project.number + ' - ' + project.title;
			}
		}
		return '';
	}

	// This is build for initial purchase request form to get current fiscal year + next 10 available
	getFiscalYears(): number[] {
		const currentFiscalYear = this.getFiscalYearFromDate(new Date());
		const options = [];
		for (let i = 0; i <= 11; i++) {
			// to get current fiscal year + next 10 available so I used 11
			options.push(currentFiscalYear + i);
		}
		return options;
	}

	static getFiscalYearRange(startYear: number, endYear: number): number[] {
		const options = [];
		for (let i = startYear; i <= endYear; i++) {
			options.push(i);
		}
		return options;
	}

	getParentGLDropdownConfigurations(
		clientId: string
	): Observable<{ hasParentFund: boolean; hasParentDepartment: boolean; hasFSLineItem: boolean }> {
		return this.clientService.getClientFeatures(clientId).pipe(
			map((res) => {
				const hasParentFund = Boolean(res.hasParentFund);
				const hasParentDepartment = Boolean(res.hasParentDepartment);
				const hasFSLineItem = Boolean(res.hasFSLineItem);
				localStorage.setItem('HasParentFund', hasParentFund.toString());
				localStorage.setItem('HasParentDepartment', hasParentDepartment.toString());
				localStorage.setItem('HasFSLineItem', hasFSLineItem.toString());
				return { hasParentFund, hasParentDepartment, hasFSLineItem };
			})
		);
	}
	async getSaveOnlyConfirmation(): Promise<boolean> {
		const result = await Swal.fire({
			title: 'Save Options',
			text: 'Do you want to submit this form, or just save the changes?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Submit',
			cancelButtonText: 'Just Save',
			customClass: {
				confirmButton: 'btn btn-success',
				cancelButton: 'btn btn-secondary',
			},
			buttonsStyling: false,
		});
		if (result.dismiss && result.dismiss !== Swal.DismissReason.cancel) {
			await Swal.fire('Action Cancelled', 'No changes were saved.', 'info');
			return null;
		}
		return result.isConfirmed;
	}

	async getDeleteConfirmation(text = "You won't be able to undo this.", title = 'Are you sure?'): Promise<boolean> {
		const result = await Swal.fire({
			title,
			text,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'I am sure!',
			cancelButtonText: 'No, cancel it!',
			customClass: {
				confirmButton: 'btn btn-success',
				cancelButton: 'btn btn-danger',
			},
			buttonsStyling: false,
		});
		return result.isConfirmed;
	}

	getTotalFileSizeFromForm(control: AbstractControl): number {
		let totalSize = 0;

		if (control instanceof FormGroup || control instanceof FormArray) {
			const controls = control instanceof FormGroup ? Object.values(control.controls) : control.controls;

			controls.forEach((child) => {
				totalSize += this.getTotalFileSizeFromForm(child);
			});
		} else {
			const file = control.value;

			if (file instanceof File && file.size) {
				totalSize += file.size;
			}
			// Handle case where it's an object that mimics a File
			else if (
				file &&
				file.file &&
				typeof file === 'object' &&
				typeof file.file.size === 'number' &&
				typeof file.file.name === 'string' &&
				typeof file.file.type === 'string'
			) {
				totalSize += file.file.size;
			}
		}

		return totalSize;
	}

	isTotalFileSizeValid(totalSize: number): boolean {
		return totalSize <= this.MAX_TOTAL_SIZE_BYTES;
	}

	getMaxSizeMB(): number {
		return this.MAX_TOTAL_SIZE_MB;
	}

	getRemainingSize(control: AbstractControl): number {
		const totalSize = this.getTotalFileSizeFromForm(control);
		const remainingSize = this.MAX_TOTAL_SIZE_BYTES - totalSize;
		return remainingSize > 0 ? remainingSize : 0;
	}

	showFileSizeExceededError(currentSizeBytes: number, maxSizeMB: number): string {
		const currentSizeMB = (currentSizeBytes / 1024 / 1024).toFixed(2);
		return `Total file size is ${currentSizeMB} MB. Please ensure all attachments are under ${maxSizeMB}MB in total.`;
	}

	showRemainingFileSizeError(fileName: string, remainingSizeBytes: number): string {
		const remainingSizeMB = (remainingSizeBytes / 1024 / 1024).toFixed(2);
		return `"${fileName}" cannot be added. You can only upload ${remainingSizeMB}MB more.`;
	}

	getFormGroup(control: AbstractControl): UntypedFormGroup {
		return control as UntypedFormGroup;
	}
	getPaymentFormTypes(): string[] {
		return [
			'cash-bond-payment-form',
			'check-request',
			'cc-approval',
			'customer-refund-form',
			'employee-reimbursable-expenses',
			'fiserv-refund-form',
			'individual-payment-form',
			'individual-refund-form',
			'ap-invoice',
			'jury-check-registry-form',
			'local-vicinity',
			'multiple-payment',
			'multiple-vendor-payment',
			'other-reimbursable-expenses',
			'other-tuition-reimbursement-payment',
			'out-of-county-travel',
			'out-of-county-travel-adjustment',
			'p-card',
			'quick-pay',
			'restitution-payment-form',
			'special-payment',
			'tax-certificate-redemption-ach-form',
			'tax-certificate-redemption-check',
			'tax-distribution',
			'travel-reimbursement-payment',
			'tuition-reimbursement',
			'void-reissue-form',
			'tuition-reimbursement-payment',
			'vendor-credit-memos',
		];
	}
	createExcelCell(text: string, styleId?: string) {
		return {
			styleId: styleId,
			data: {
				type: 'String',
				value: text,
			},
		};
	}

	getCountries() {
		return [
			{
				name: 'Afghanistan',
				codeA2: 'AF',
				codeA3: 'AFG',
			},
			{
				name: 'Åland Islands',
				codeA2: 'AX',
				codeA3: 'ALA',
			},
			{ name: 'Albania', codeA2: 'AL', codeA3: 'ALB' },
			{
				name: 'Algeria',
				codeA2: 'DZ',
				codeA3: 'DZA',
			},
			{
				name: 'American Samoa',
				codeA2: 'AS',
				codeA3: 'ASM',
			},
			{
				name: 'Andorra',
				codeA2: 'AD',
				codeA3: 'AND',
			},
			{
				name: 'Angola',
				codeA2: 'AO',
				codeA3: 'AGO',
			},
			{
				name: 'Anguilla',
				codeA2: 'AI',
				codeA3: 'AIA',
			},
			{
				name: 'Antarctica',
				codeA2: 'AQ',
				codeA3: 'ATA',
			},
			{
				name: 'Antigua and Barbuda',
				codeA2: 'AG',
				codeA3: 'ATG',
			},
			{
				name: 'Argentina',
				codeA2: 'AR',
				codeA3: 'ARG',
			},
			{ name: 'Armenia', codeA2: 'AM', codeA3: 'ARM' },
			{
				name: 'Aruba',
				codeA2: 'AW',
				codeA3: 'ABW',
			},
			{
				name: 'Australia',
				codeA2: 'AU',
				codeA3: 'AUS',
			},
			{ name: 'Austria', codeA2: 'AT', codeA3: 'AUT' },
			{
				name: 'Azerbaijan',
				codeA2: 'AZ',
				codeA3: 'AZE',
			},
			{
				name: 'Bahamas',
				codeA2: 'BS',
				codeA3: 'BHS',
			},
			{
				name: 'Bahrain',
				codeA2: 'BH',
				codeA3: 'BHR',
			},
			{
				name: 'Bangladesh',
				codeA2: 'BD',
				codeA3: 'BGD',
			},
			{
				name: 'Barbados',
				codeA2: 'BB',
				codeA3: 'BRB',
			},
			{
				name: 'Belarus',
				codeA2: 'BY',
				codeA3: 'BLR',
			},
			{ name: 'Belgium', codeA2: 'BE', codeA3: 'BEL' },
			{
				name: 'Belize',
				codeA2: 'BZ',
				codeA3: 'BLZ',
			},
			{
				name: 'Benin',
				codeA2: 'BJ',
				codeA3: 'BEN',
			},
			{
				name: 'Bermuda',
				codeA2: 'BM',
				codeA3: 'BMU',
			},
			{
				name: 'Bhutan',
				codeA2: 'BT',
				codeA3: 'BTN',
			},
			{ name: 'Bolivia', codeA2: 'BO', codeA3: 'BOL' },
			{
				name: 'Bonaire, Sint Eustatius and Saba',
				codeA2: 'BQ',
				codeA3: 'BES',
			},
			{
				name: 'Bosnia and Herzegovina',
				codeA2: 'BA',
				codeA3: 'BIH',
			},
			{
				name: 'Botswana',
				codeA2: 'BW',
				codeA3: 'BWA',
			},
			{
				name: 'Bouvet Island',
				codeA2: 'BV',
				codeA3: 'BVT',
			},
			{
				name: 'Brazil',
				codeA2: 'BR',
				codeA3: 'BRA',
			},
			{
				name: 'British Indian Ocean Territory',
				codeA2: 'IO',
				codeA3: 'IOT',
			},
			{
				name: 'Brunei Darussalam',
				codeA2: 'BN',
				codeA3: 'BRN',
			},
			{
				name: 'Bulgaria',
				codeA2: 'BG',
				codeA3: 'BGR',
			},
			{
				name: 'Burkina Faso',
				codeA2: 'BF',
				codeA3: 'BFA',
			},
			{
				name: 'Burundi',
				codeA2: 'BI',
				codeA3: 'BDI',
			},
			{
				name: 'Cambodia',
				codeA2: 'KH',
				codeA3: 'KHM',
			},
			{
				name: 'Cameroon',
				codeA2: 'CM',
				codeA3: 'CMR',
			},
			{
				name: 'Canada',
				codeA2: 'CA',
				codeA3: 'CAN',
			},
			{
				name: 'Cape Verde',
				codeA2: 'CV',
				codeA3: 'CPV',
			},
			{
				name: 'Cayman Islands',
				codeA2: 'KY',
				codeA3: 'CYM',
			},
			{
				name: 'Central African Republic',
				codeA2: 'CF',
				codeA3: 'CAF',
			},
			{
				name: 'Chad',
				codeA2: 'TD',
				codeA3: 'TCD',
			},
			{
				name: 'Chile',
				codeA2: 'CL',
				codeA3: 'CHL',
			},
			{
				name: 'China',
				codeA2: 'CN',
				codeA3: 'CHN',
			},
			{
				name: 'Christmas Island',
				codeA2: 'CX',
				codeA3: 'CXR',
			},
			{
				name: 'Cocos (Keeling) Islands',
				codeA2: 'CC',
				codeA3: 'CCK',
			},
			{
				name: 'Colombia',
				codeA2: 'CO',
				codeA3: 'COL',
			},
			{ name: 'Comoros', codeA2: 'KM', codeA3: 'COM' },
			{ name: 'Congo', codeA2: 'CG', codeA3: 'COG' },
			{ name: 'Congo, Democratic Republic of the', codeA2: 'CD', codeA3: 'COD' },
			{ name: 'Cook Islands', codeA2: 'CK', codeA3: 'COK' },
			{ name: 'Costa Rica', codeA2: 'CR', codeA3: 'CRI' },
			{ name: "Côte d'Ivoire", codeA2: 'CI', codeA3: 'CIV' },
			{ name: 'Croatia', codeA2: 'HR', codeA3: 'HRV' },
			{ name: 'Cuba', codeA2: 'CU', codeA3: 'CUB' },
			{ name: 'Curaçao', codeA2: 'CW', codeA3: 'CUW' },
			{ name: 'Cyprus', codeA2: 'CY', codeA3: 'CYP' },
			{ name: 'Czech Republic', codeA2: 'CZ', codeA3: 'CZE' },
			{ name: 'Denmark', codeA2: 'DK', codeA3: 'DNK' },
			{ name: 'Djibouti', codeA2: 'DJ', codeA3: 'DJI' },
			{ name: 'Dominica', codeA2: 'DM', codeA3: 'DMA' },
			{ name: 'Dominican Republic', codeA2: 'DO', codeA3: 'DOM' },
			{ name: 'Ecuador', codeA2: 'EC', codeA3: 'ECU' },
			{ name: 'Egypt', codeA2: 'EG', codeA3: 'EGY' },
			{ name: 'El Salvador', codeA2: 'SV', codeA3: 'SLV' },
			{ name: 'Equatorial Guinea', codeA2: 'GQ', codeA3: 'GNQ' },
			{ name: 'Eritrea', codeA2: 'ER', codeA3: 'ERI' },
			{ name: 'Estonia', codeA2: 'EE', codeA3: 'EST' },
			{ name: 'Eswatini', codeA2: 'SZ', codeA3: 'SWZ' },
			{ name: 'Ethiopia', codeA2: 'ET', codeA3: 'ETH' },
			{ name: 'Falkland Islands (Malvinas)', codeA2: 'FK', codeA3: 'FLK' },
			{ name: 'Faroe Islands', codeA2: 'FO', codeA3: 'FRO' },
			{ name: 'Fiji', codeA2: 'FJ', codeA3: 'FJI' },
			{ name: 'Finland', codeA2: 'FI', codeA3: 'FIN' },
			{
				name: 'France',
				codeA2: 'FR',
				codeA3: 'FRA',
			},
			{ name: 'French Guiana', codeA2: 'GF', codeA3: 'GUF' },
			{ name: 'French Polynesia', codeA2: 'PF', codeA3: 'PYF' },
			{ name: 'French Southern Territories', codeA2: 'TF', codeA3: 'ATF' },
			{ name: 'Gabon', codeA2: 'GA', codeA3: 'GAB' },
			{ name: 'Gambia', codeA2: 'GM', codeA3: 'GMB' },
			{ name: 'Georgia', codeA2: 'GE', codeA3: 'GEO' },
			{
				name: 'Germany',
				codeA2: 'DE',
				codeA3: 'DEU',
			},
			{ name: 'Ghana', codeA2: 'GH', codeA3: 'GHA' },
			{ name: 'Gibraltar', codeA2: 'GI', codeA3: 'GIB' },
			{ name: 'Greece', codeA2: 'GR', codeA3: 'GRC' },
			{ name: 'Greenland', codeA2: 'GL', codeA3: 'GRL' },
			{ name: 'Grenada', codeA2: 'GD', codeA3: 'GRD' },
			{ name: 'Guadeloupe', codeA2: 'GP', codeA3: 'GLP' },
			{ name: 'Guam', codeA2: 'GU', codeA3: 'GUM' },
			{ name: 'Guatemala', codeA2: 'GT', codeA3: 'GTM' },
			{ name: 'Guernsey', codeA2: 'GG', codeA3: 'GGY' },
			{ name: 'Guinea', codeA2: 'GN', codeA3: 'GIN' },
			{ name: 'Guinea-Bissau', codeA2: 'GW', codeA3: 'GNB' },
			{ name: 'Guyana', codeA2: 'GY', codeA3: 'GUY' },
			{ name: 'Haiti', codeA2: 'HT', codeA3: 'HTI' },
			{ name: 'Heard Island and McDonald Islands', codeA2: 'HM', codeA3: 'HMD' },
			{ name: 'Holy See (Vatican City State)', codeA2: 'VA', codeA3: 'VAT' },
			{ name: 'Honduras', codeA2: 'HN', codeA3: 'HND' },
			{ name: 'Hong Kong', codeA2: 'HK', codeA3: 'HKG' },
			{ name: 'Hungary', codeA2: 'HU', codeA3: 'HUN' },
			{ name: 'Iceland', codeA2: 'IS', codeA3: 'ISL' },
			{
				name: 'India',
				codeA2: 'IN',
				codeA3: 'IND',
			},
			{ name: 'Indonesia', codeA2: 'ID', codeA3: 'IDN' },
			{ name: 'Iran, Islamic Republic of', codeA2: 'IR', codeA3: 'IRN' },
			{ name: 'Iraq', codeA2: 'IQ', codeA3: 'IRQ' },
			{ name: 'Ireland', codeA2: 'IE', codeA3: 'IRL' },
			{ name: 'Isle of Man', codeA2: 'IM', codeA3: 'IMN' },
			{ name: 'Israel', codeA2: 'IL', codeA3: 'ISR' },
			{ name: 'Italy', codeA2: 'IT', codeA3: 'ITA' },
			{ name: 'Jamaica', codeA2: 'JM', codeA3: 'JAM' },
			{ name: 'Japan', codeA2: 'JP', codeA3: 'JPN' },
			{ name: 'Jersey', codeA2: 'JE', codeA3: 'JEY' },
			{ name: 'Jordan', codeA2: 'JO', codeA3: 'JOR' },
			{ name: 'Kazakhstan', codeA2: 'KZ', codeA3: 'KAZ' },
			{ name: 'Kenya', codeA2: 'KE', codeA3: 'KEN' },
			{ name: 'Kiribati', codeA2: 'KI', codeA3: 'KIR' },
			{ name: "Korea, Democratic People's Republic of", codeA2: 'KP', codeA3: 'PRK' },
			{ name: 'Korea, Republic of', codeA2: 'KR', codeA3: 'KOR' },
			{ name: 'Kuwait', codeA2: 'KW', codeA3: 'KWT' },
			{ name: 'Kyrgyzstan', codeA2: 'KG', codeA3: 'KGZ' },
			{ name: "Lao People's Democratic Republic", codeA2: 'LA', codeA3: 'LAO' },
			{ name: 'Latvia', codeA2: 'LV', codeA3: 'LVA' },
			{ name: 'Lebanon', codeA2: 'LB', codeA3: 'LBN' },
			{ name: 'Lesotho', codeA2: 'LS', codeA3: 'LSO' },
			{ name: 'Liberia', codeA2: 'LR', codeA3: 'LBR' },
			{ name: 'Libya', codeA2: 'LY', codeA3: 'LBY' },
			{ name: 'Liechtenstein', codeA2: 'LI', codeA3: 'LIE' },
			{ name: 'Lithuania', codeA2: 'LT', codeA3: 'LTU' },
			{ name: 'Luxembourg', codeA2: 'LU', codeA3: 'LUX' },
			{ name: 'Macao', codeA2: 'MO', codeA3: 'MAC' },
			{ name: 'North Macedonia', codeA2: 'MK', codeA3: 'MKD' },
			{ name: 'Madagascar', codeA2: 'MG', codeA3: 'MDG' },
			{ name: 'Malawi', codeA2: 'MW', codeA3: 'MWI' },
			{ name: 'Malaysia', codeA2: 'MY', codeA3: 'MYS' },
			{ name: 'Maldives', codeA2: 'MV', codeA3: 'MDV' },
			{ name: 'Mali', codeA2: 'ML', codeA3: 'MLI' },
			{ name: 'Malta', codeA2: 'MT', codeA3: 'MLT' },
			{ name: 'Marshall Islands', codeA2: 'MH', codeA3: 'MHL' },
			{ name: 'Martinique', codeA2: 'MQ', codeA3: 'MTQ' },
			{ name: 'Mauritania', codeA2: 'MR', codeA3: 'MRT' },
			{ name: 'Mauritius', codeA2: 'MU', codeA3: 'MUS' },
			{ name: 'Mayotte', codeA2: 'YT', codeA3: 'MYT' },
			{
				name: 'Mexico',
				codeA2: 'MX',
				codeA3: 'MEX',
			},
			{ name: 'Micronesia, Federated States of', codeA2: 'FM', codeA3: 'FSM' },
			{ name: 'Moldova, Republic of', codeA2: 'MD', codeA3: 'MDA' },
			{ name: 'Monaco', codeA2: 'MC', codeA3: 'MCO' },
			{ name: 'Mongolia', codeA2: 'MN', codeA3: 'MNG' },
			{ name: 'Montenegro', codeA2: 'ME', codeA3: 'MNE' },
			{ name: 'Montserrat', codeA2: 'MS', codeA3: 'MSR' },
			{ name: 'Morocco', codeA2: 'MA', codeA3: 'MAR' },
			{ name: 'Mozambique', codeA2: 'MZ', codeA3: 'MOZ' },
			{ name: 'Myanmar', codeA2: 'MM', codeA3: 'MMR' },
			{ name: 'Namibia', codeA2: 'NA', codeA3: 'NAM' },
			{ name: 'Nauru', codeA2: 'NR', codeA3: 'NRU' },
			{ name: 'Nepal', codeA2: 'NP', codeA3: 'NPL' },
			{ name: 'Netherlands', codeA2: 'NL', codeA3: 'NLD' },
			{ name: 'New Caledonia', codeA2: 'NC', codeA3: 'NCL' },
			{ name: 'New Zealand', codeA2: 'NZ', codeA3: 'NZL' },
			{ name: 'Nicaragua', codeA2: 'NI', codeA3: 'NIC' },
			{ name: 'Niger', codeA2: 'NE', codeA3: 'NER' },
			{ name: 'Nigeria', codeA2: 'NG', codeA3: 'NGA' },
			{ name: 'Niue', codeA2: 'NU', codeA3: 'NIU' },
			{ name: 'Norfolk Island', codeA2: 'NF', codeA3: 'NFK' },
			{ name: 'Northern Mariana Islands', codeA2: 'MP', codeA3: 'MNP' },
			{ name: 'Norway', codeA2: 'NO', codeA3: 'NOR' },
			{ name: 'Oman', codeA2: 'OM', codeA3: 'OMN' },
			{ name: 'Pakistan', codeA2: 'PK', codeA3: 'PAK' },
			{ name: 'Palau', codeA2: 'PW', codeA3: 'PLW' },
			{ name: 'Palestine, State of', codeA2: 'PS', codeA3: 'PSE' },
			{ name: 'Panama', codeA2: 'PA', codeA3: 'PAN' },
			{ name: 'Papua New Guinea', codeA2: 'PG', codeA3: 'PNG' },
			{ name: 'Paraguay', codeA2: 'PY', codeA3: 'PRY' },
			{ name: 'Peru', codeA2: 'PE', codeA3: 'PER' },
			{ name: 'Philippines', codeA2: 'PH', codeA3: 'PHL' },
			{ name: 'Pitcairn', codeA2: 'PN', codeA3: 'PCN' },
			{ name: 'Poland', codeA2: 'PL', codeA3: 'POL' },
			{ name: 'Portugal', codeA2: 'PT', codeA3: 'PRT' },
			{ name: 'Puerto Rico', codeA2: 'PR', codeA3: 'PRI' },
			{ name: 'Qatar', codeA2: 'QA', codeA3: 'QAT' },
			{ name: 'Réunion', codeA2: 'RE', codeA3: 'REU' },
			{ name: 'Romania', codeA2: 'RO', codeA3: 'ROU' },
			{ name: 'Russian Federation', codeA2: 'RU', codeA3: 'RUS' },
			{ name: 'Rwanda', codeA2: 'RW', codeA3: 'RWA' },
			{ name: 'Saint Barthélemy', codeA2: 'BL', codeA3: 'BLM' },
			{ name: 'Saint Helena, Ascension and Tristan da Cunha', codeA2: 'SH', codeA3: 'SHN' },
			{ name: 'Saint Kitts and Nevis', codeA2: 'KN', codeA3: 'KNA' },
			{ name: 'Saint Lucia', codeA2: 'LC', codeA3: 'LCA' },
			{ name: 'Saint Martin (French part)', codeA2: 'MF', codeA3: 'MAF' },
			{ name: 'Saint Pierre and Miquelon', codeA2: 'PM', codeA3: 'SPM' },
			{ name: 'Saint Vincent and the Grenadines', codeA2: 'VC', codeA3: 'VCT' },
			{ name: 'Samoa', codeA2: 'WS', codeA3: 'WSM' },
			{ name: 'San Marino', codeA2: 'SM', codeA3: 'SMR' },
			{ name: 'Sao Tome and Principe', codeA2: 'ST', codeA3: 'STP' },
			{ name: 'Saudi Arabia', codeA2: 'SA', codeA3: 'SAU' },
			{ name: 'Senegal', codeA2: 'SN', codeA3: 'SEN' },
			{ name: 'Serbia', codeA2: 'RS', codeA3: 'SRB' },
			{ name: 'Seychelles', codeA2: 'SC', codeA3: 'SYC' },
			{ name: 'Sierra Leone', codeA2: 'SL', codeA3: 'SLE' },
			{ name: 'Singapore', codeA2: 'SG', codeA3: 'SGP' },
			{ name: 'Sint Maarten (Dutch part)', codeA2: 'SX', codeA3: 'SXM' },
			{ name: 'Slovakia', codeA2: 'SK', codeA3: 'SVK' },
			{ name: 'Slovenia', codeA2: 'SI', codeA3: 'SVN' },
			{ name: 'Solomon Islands', codeA2: 'SB', codeA3: 'SLB' },
			{ name: 'Somalia', codeA2: 'SO', codeA3: 'SOM' },
			{ name: 'South Africa', codeA2: 'ZA', codeA3: 'ZAF' },
			{ name: 'South Georgia and the South Sandwich Islands', codeA2: 'GS', codeA3: 'SGS' },
			{ name: 'South Sudan', codeA2: 'SS', codeA3: 'SSD' },
			{ name: 'Spain', codeA2: 'ES', codeA3: 'ESP' },
			{ name: 'Sri Lanka', codeA2: 'LK', codeA3: 'LKA' },
			{ name: 'Sudan', codeA2: 'SD', codeA3: 'SDN' },
			{ name: 'Suriname', codeA2: 'SR', codeA3: 'SUR' },
			{ name: 'Svalbard and Jan Mayen', codeA2: 'SJ', codeA3: 'SJM' },
			{ name: 'Sweden', codeA2: 'SE', codeA3: 'SWE' },
			{ name: 'Switzerland', codeA2: 'CH', codeA3: 'CHE' },
			{ name: 'Syrian Arab Republic', codeA2: 'SY', codeA3: 'SYR' },
			{ name: 'Taiwan', codeA2: 'TW', codeA3: 'TWN' },
			{ name: 'Tajikistan', codeA2: 'TJ', codeA3: 'TJK' },
			{ name: 'Tanzania, United Republic of', codeA2: 'TZ', codeA3: 'TZA' },
			{ name: 'Thailand', codeA2: 'TH', codeA3: 'THA' },
			{ name: 'Timor-Leste', codeA2: 'TL', codeA3: 'TLS' },
			{ name: 'Togo', codeA2: 'TG', codeA3: 'TGO' },
			{ name: 'Tokelau', codeA2: 'TK', codeA3: 'TKL' },
			{ name: 'Tonga', codeA2: 'TO', codeA3: 'TON' },
			{ name: 'Trinidad and Tobago', codeA2: 'TT', codeA3: 'TTO' },
			{ name: 'Tunisia', codeA2: 'TN', codeA3: 'TUN' },
			{ name: 'Turkey', codeA2: 'TR', codeA3: 'TUR' },
			{ name: 'Turkmenistan', codeA2: 'TM', codeA3: 'TKM' },
			{ name: 'Turks and Caicos Islands', codeA2: 'TC', codeA3: 'TCA' },
			{ name: 'Tuvalu', codeA2: 'TV', codeA3: 'TUV' },
			{ name: 'Uganda', codeA2: 'UG', codeA3: 'UGA' },
			{ name: 'Ukraine', codeA2: 'UA', codeA3: 'UKR' },
			{ name: 'United Arab Emirates', codeA2: 'AE', codeA3: 'ARE' },
			{
				name: 'United Kingdom',
				codeA2: 'GB',
				codeA3: 'GBR',
			},
			{
				name: 'United States',
				codeA2: 'US',
				codeA3: 'USA',
			},
			{ name: 'United States Minor Outlying Islands', codeA2: 'UM', codeA3: 'UMI' },
			{ name: 'Uruguay', codeA2: 'UY', codeA3: 'URY' },
			{ name: 'Uzbekistan', codeA2: 'UZ', codeA3: 'UZB' },
			{ name: 'Vanuatu', codeA2: 'VU', codeA3: 'VUT' },
			{ name: 'Venezuela', codeA2: 'VE', codeA3: 'VEN' },
			{ name: 'Viet Nam', codeA2: 'VN', codeA3: 'VNM' },
			{ name: 'Virgin Islands, British', codeA2: 'VG', codeA3: 'VGB' },
			{ name: 'Virgin Islands, U.S.', codeA2: 'VI', codeA3: 'VIR' },
			{ name: 'Wallis and Futuna', codeA2: 'WF', codeA3: 'WLF' },
			{ name: 'Western Sahara', codeA2: 'EH', codeA3: 'ESH' },
			{ name: 'Yemen', codeA2: 'YE', codeA3: 'YEM' },
			{ name: 'Zambia', codeA2: 'ZM', codeA3: 'ZMB' },
			{ name: 'Zimbabwe', codeA2: 'ZW', codeA3: 'ZWE' },
		];
	}
	hasRequiredAttachment(
		newFileAttachments: any,
		existingAttachments: any[] = [],
		deletedDocumentIds: any[] = []
	): boolean {
		const hasNewFiles =
			(newFileAttachments?.files?.length ?? 0) > 0 ||
			(Array.isArray(newFileAttachments) && newFileAttachments.length > 0) ||
			(newFileAttachments?.formArray?.value?.some?.((v: any) => v?.file != null || v?.attachment != null) ?? false) ||
			!!(newFileAttachments?.monthlyStatement ?? newFileAttachments?.singleFile);
		const deletedSet = new Set(deletedDocumentIds?.map((id) => String(id)) ?? []);
		const hasExistingNotDeleted = existingAttachments?.some(
			(attachment) => attachment && !deletedSet.has(String(attachment.id ?? attachment.documentId ?? ''))
		);
		return hasNewFiles || !!hasExistingNotDeleted;
	}
	setFileNameWithPrefix(file: File): File {
		const uniquePrefix = this.getRandomText(8);
		const newName = `${uniquePrefix}-${file.name}`;
		return new File([file], newName, file);
	}
	setCalendarRange(e: any, month: number | null, year: number | null, defaultToStart: boolean = true) {
		const applyDatepicker = (
			element: any,
			minDate: Date | null,
			maxDate: Date | null,
			defaultDate: Date | null = null
		) => {
			$(() => {
				if (element.hasClass('hasDatepicker')) {
					element.datepicker('option', {
						dateFormat: 'mm/dd/yy',
						minDate,
						maxDate,
						defaultDate,
					});
				} else {
					element.datepicker({
						dateFormat: 'mm/dd/yy',
						minDate,
						maxDate,
						defaultDate,
					});
				}
				element.prop('disabled', false).val('');
			});
		};
		let minDate: Date | null = null;
		let maxDate: Date | null = null;

		if (year && month) {
			const effectiveYear = this.getEffectiveYear(month, year);
			minDate = new Date(effectiveYear, month - 1, 1);
			maxDate = new Date(effectiveYear, month, 0);
		} else if (year && !month) {
			const fiscalYearMonthBegin = this.getFiscalYearMonthBegin();
			if (fiscalYearMonthBegin === 1) {
				minDate = new Date(year, 0, 1);
				maxDate = new Date(year, 11, 31);
			} else {
				minDate = new Date(year - 1, fiscalYearMonthBegin - 1, 1);
				maxDate = new Date(year, fiscalYearMonthBegin - 1, 0);
			}
		}

		const defaultDate = minDate && maxDate ? (defaultToStart ? minDate : maxDate) : null;
		applyDatepicker(e, minDate, maxDate, defaultDate);
	}
	/**
	 * Converts C# TimeSpan format (HH:mm:ss) to HTML5 time input format (HH:mm)
	 * @param time - Time string in format "HH:mm:ss" or null
	 * @returns Formatted time string "HH:mm" or null if invalid
	 */
	convertTimeSpanToHHmm(time: string | null): string | null {
		if (typeof time !== 'string') return null;

		// Validate HH:mm:ss format with proper ranges (00-23 hours, 00-59 minutes/seconds)
		const match = time.match(/^([01]\d|2[0-3]):([0-5]\d):[0-5]\d$/);
		return match ? `${match[1]}:${match[2]}` : null;
	}

	/**
	 * Creates a standardized date picker blur handler function.
	 * This utility extracts common date validation logic used across multiple payment form components.
	 *
	 * @param context - The component instance (typically 'this' or 'self') that provides access to the component's state
	 * @param formControl - The form control to update when the date value changes
	 * @param datesValidFlag - Property name on the context object that tracks if dates are valid (e.g., 'areDatesValid')
	 * @returns A jQuery event handler function for the blur event
	 */
	createDatePickerBlurHandler(
		context: DatePickerContext,
		formControl: AbstractControl | null,
		datesValidFlag: string = 'areDatesValid'
	): (event: Event) => void {
		return function (event: Event) {
			if (!event || !event.target) {
				return;
			}

			const element = $(event.target);
			const newDate = element.val() as string;

			if (newDate) {
				if (!moment(newDate, 'MM/DD/YYYY', true).isValid()) {
					context[datesValidFlag] = false;
					element.addClass('has-error');
					if (formControl) {
						formControl.setValue(null);
					}
				} else {
					context[datesValidFlag] = true;
					element.removeClass('has-error');
					if (formControl) {
						formControl.setValue(newDate);
					}
				}
			} else {
				context[datesValidFlag] = true;
				element.removeClass('has-error');
				if (formControl) {
					formControl.setValue(null);
				}
			}
		};
	}

	/**
	 * Creates a standardized date picker blur handler function for dynamic/indexed form controls.
	 * This is useful for components with multiple dynamic statements where form controls are accessed via array indices.
	 *
	 * @param context - The component instance (typically 'this' or 'self') that provides access to the component's state
	 * @param getFormControl - Function that returns the form control to update, receives the jQuery element as parameter
	 * @param datesValidFlag - Property name on the context object that tracks if dates are valid (e.g., 'areDatesValid')
	 * @returns A jQuery event handler function for the blur event
	 */
	createIndexedDatePickerBlurHandler(
		context: DatePickerContext,
		getFormControl: (element: JQuery) => AbstractControl | null,
		datesValidFlag: string = 'areDatesValid'
	): (event: Event) => void {
		return function (event: Event) {
			if (!event || !event.target) {
				return;
			}

			const element = $(event.target);
			const formControl = getFormControl(element);
			const newDate = element.val() as string;

			if (newDate) {
				if (!moment(newDate, 'MM/DD/YYYY', true).isValid()) {
					context[datesValidFlag] = false;
					element.addClass('has-error');
					if (formControl) {
						formControl.setValue(null);
					}
				} else {
					context[datesValidFlag] = true;
					element.removeClass('has-error');
					if (formControl) {
						formControl.setValue(newDate);
					}
				}
			} else {
				context[datesValidFlag] = true;
				element.removeClass('has-error');
				if (formControl) {
					formControl.setValue(null);
				}
			}
		};
	}
	formatCurrencyWithPrecision(currency: number, decimalPlaces: number) {
		try {
			const converted = Number(currency);
			if (converted || converted === 0) {
				let changedVal = converted;
				if (converted < 0) {
					changedVal *= -1;
				}
				const formatted = changedVal.toLocaleString(undefined, {
					minimumFractionDigits: decimalPlaces,
					maximumFractionDigits: decimalPlaces,
				});
				if (converted >= 0) {
					return `$${formatted}`;
				}
				return `($${formatted})`;
			}
		} catch (e) {
			console.log(e);
		}
		return '--';
	}
	isValidEmail(email: string): boolean {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(email);
	}

	isValidDomain(domain: string): boolean {
		const re = /^(?!:\/\/)([a-zA-Z0-9_-]+\.)+[a-zA-Z]{2,}$/;
		return re.test(domain);
	}
	setClientRequiredFieldConfigValidation(
		form: FormGroup,
		fieldList: { fieldName: string; isRequired: boolean }[]
	): void {
		if (!fieldList || fieldList.length === 0) {
			return;
		}
		const updatedFields: string[] = [];
		const unchangedFields: string[] = [];
		this.requiredFieldMap = {};

		Object.keys(form.controls).forEach((key) => {
			const control = form.get(key);

			if (control instanceof FormArray) {
				control.controls.forEach((group: UntypedFormGroup, index) => {
					Object.entries((group as UntypedFormGroup).controls).forEach(([childKey, childControl]) => {
						const fullPath = `${key}.0.${childKey}`;
						const fullPathWithIndex = `${key}.${index}.${childKey}`;
						const config = fieldList.find((f) => f.fieldName === fullPath);
						if (!config) return;
						this.requiredFieldMap[childKey] = config.isRequired;

						const hasRequired = childControl.hasValidator(Validators.required);
						const shouldBeRequired = config?.isRequired;

						if (shouldBeRequired && !hasRequired) {
							childControl.addValidators(Validators.required);
							updatedFields.push(`${fullPathWithIndex} → required added`);
						} else if (!shouldBeRequired && hasRequired) {
							childControl.removeValidators(Validators.required);
							updatedFields.push(`${fullPathWithIndex} → required removed`);
						} else {
							unchangedFields.push(fullPathWithIndex);
						}

						childControl.updateValueAndValidity();
					});
				});

				fieldList
					.filter((f) => f.fieldName.startsWith(`${key}.0.`))
					.forEach((config) => {
						const childKey = config.fieldName.split('.').pop();
						if (childKey && !(childKey in this.requiredFieldMap)) {
							this.requiredFieldMap[childKey] = config.isRequired;
						}
					});
				return;
			}

			const configField =
				fieldList.find((f) => f.fieldName === key) || fieldList.find((f) => f.fieldName.endsWith(`.${key}`));
			if (!configField) {
				return;
			}
			if (control) {
				const hasRequired = control.hasValidator(Validators.required);
				const shouldBeRequired = configField?.isRequired ?? hasRequired;
				this.requiredFieldMap[key] = shouldBeRequired;

				if (shouldBeRequired && !hasRequired) {
					control.addValidators(Validators.required);
					updatedFields.push(`${key} → required added`);
				} else if (!shouldBeRequired && hasRequired) {
					control.removeValidators(Validators.required);
					updatedFields.push(`${key} → required removed`);
				} else {
					unchangedFields.push(key);
				}

				control.updateValueAndValidity();
				if (!shouldBeRequired && !control.value) {
					control.setErrors(null);
				}
			}
		});
		fieldList.forEach((field) => {
			const key = field.fieldName.includes('.') ? field.fieldName.split('.').pop() : field.fieldName;
			if (key && !(key in this.requiredFieldMap)) {
				this.requiredFieldMap[key] = field.isRequired;
			}
		});
	}

	initDatePicker(
		elementId: string,
		context: DatePickerContext,
		formControl: AbstractControl | null,
		datesValidFlag: string = 'areDatesValid',
		maxRetries = 10,
		retryDelayMs = 300
	): void {
		if (typeof $ === 'undefined') {
			return;
		}
		const attach = (retryCount: number): void => {
			const element = $(`#${elementId}`);
			if (!element.length) {
				if (retryCount < maxRetries) {
					setTimeout(() => attach(retryCount + 1), retryDelayMs);
				}
				return;
			}
			// Idempotent: tear down any prior instance so re-init (e.g. a reload
			// or config re-read) does not stack duplicate pickers / blur handlers.
			// jQuery-UI augments jQuery with datepicker(); it is not in the base
			// JQuery type here, so reference it through an any-typed alias.
			const datepickerEl = element as any;
			try {
				if (element.hasClass('hasDatepicker')) {
					datepickerEl.datepicker('destroy');
				}
			} catch {
				// ignore a missing prior datepicker instance
			}
			element.off('blur');
			datepickerEl.datepicker({
				onSelect: (date: string) => {
					if (date && !moment(date, 'MM/DD/YYYY', true).isValid()) {
						context[datesValidFlag] = false;
						element.addClass('has-error');
						if (formControl) {
							formControl.setValue(null);
						}
					} else {
						context[datesValidFlag] = true;
						element.removeClass('has-error');
						if (formControl) {
							formControl.setValue(date);
						}
					}
				},
			});
			element.on('blur', this.createDatePickerBlurHandler(context, formControl, datesValidFlag));
		};
		attach(0);
	}
}
